import { getToken } from 'firebase/messaging';

import {
  NotificationBrowserBlockedError,
  NotificationPermissionDeniedError,
  NotificationPushServiceError,
  NotificationUnsupportedError,
} from '@/features/notifications/errors';
import { messaging } from '@/integrations/firebase/firebase';
import { getEnvVar } from '@/shared/utils/envVarConfig';

interface SwParams {
  apiKey: string;
  authDomain: string;
  projectId: string;
  senderId: string;
  appId: string;
  version?: string; // for cache-busting
}

export function buildSwUrl(p: SwParams) {
  const q = new URLSearchParams({
    apiKey: p.apiKey,
    authDomain: p.authDomain,
    projectId: p.projectId,
    senderId: p.senderId,
    appId: p.appId,
    v: p.version ?? String(Date.now()),
  });
  return `/firebase-messaging-sw.js?${q.toString()}`;
}

/**
 * Check current web push notification permission status
 */
export function checkWebPermissions(): boolean {
  if (!('Notification' in window)) {
    return false;
  }
  return Notification.permission === 'granted';
}

/**
 * Check if web push notifications are blocked by the browser
 */
export function isWebPermissionDenied(): boolean {
  if (!('Notification' in window)) {
    return false;
  }
  return Notification.permission === 'denied';
}

export async function ensurePermission(): Promise<boolean> {
  const perm = await Notification.requestPermission();
  return perm === 'granted';
}

export async function registerSw(swUrl: string) {
  return navigator.serviceWorker.register(swUrl);
}

export function getWebFcmToken(args: { swReg?: ServiceWorkerRegistration }) {
  if (!messaging) {
    return undefined;
  }

  return getToken(messaging, {
    vapidKey: getEnvVar('VITE_FIREBASE_WEBPUSH_VAPID_KEY'),
    serviceWorkerRegistration: args.swReg,
  });
}

/**
 * Wait for the service worker to be fully active.
 */
async function waitForSwActive(
  registration: ServiceWorkerRegistration,
  timeoutMs = 15000, // 15 second timeout
): Promise<ServiceWorkerRegistration> {
  if (registration.active) return registration;

  const sw = registration.installing ?? registration.waiting;
  if (!sw) throw new Error('No service worker found');
  if (sw.state === 'activated') return registration;

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      sw.removeEventListener('statechange', onStateChange);
      reject(new Error('Service worker activation timeout'));
    }, timeoutMs);

    const onStateChange = () => {
      if (sw.state === 'activated') {
        clearTimeout(timeout);
        sw.removeEventListener('statechange', onStateChange);
        resolve(registration);
      } else if (sw.state === 'redundant') {
        clearTimeout(timeout);
        sw.removeEventListener('statechange', onStateChange);
        reject(new Error('Service worker became redundant'));
      }
    };
    sw.addEventListener('statechange', onStateChange);
  });
}

export interface GetWebTokenOptions {
  /**
   * If true, only checks if permission is already granted (no popup).
   * If false (default), requests permission from user (shows browser popup).
   */
  silent?: boolean;
}

/**
 * Get FCM token for web platform via Firebase Messaging.
 *
 * @param options.silent - If true, only proceeds if permission already granted.
 *                         Use for background token refresh. Default: false.
 * @returns The FCM token.
 * @throws {NotificationUnsupportedError} If web push is not supported.
 * @throws {NotificationPermissionDeniedError} If permission was denied (default state).
 * @throws {NotificationBrowserBlockedError} If notifications are blocked in settings.
 */
export async function getWebToken(
  options?: GetWebTokenOptions,
): Promise<string> {
  if (!messaging || !('Notification' in window)) {
    throw new NotificationUnsupportedError(
      'Web push notifications are not supported in this browser',
    );
  }

  // Check if already blocked before requesting
  if (isWebPermissionDenied()) {
    throw new NotificationBrowserBlockedError();
  }

  const permitted = options?.silent
    ? checkWebPermissions()
    : await ensurePermission();

  if (!permitted) {
    // After request, check if it's now blocked or just denied
    if (isWebPermissionDenied()) {
      throw new NotificationBrowserBlockedError();
    }
    throw new NotificationPermissionDeniedError('WEB');
  }

  const swUrl = buildSwUrl({
    apiKey: getEnvVar('VITE_FIREBASE_API_KEY'),
    authDomain: getEnvVar('VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: getEnvVar('VITE_FIREBASE_PROJECT_ID'),
    senderId: getEnvVar('VITE_FIREBASE_MESSAGING_SENDER_ID'),
    appId: getEnvVar('VITE_FIREBASE_APP_ID'),
  });

  const swReg = await registerSw(swUrl);
  await waitForSwActive(swReg);

  let token: string | undefined;
  try {
    token = await getWebFcmToken({ swReg });
  } catch (error) {
    // Handle Push API errors (common with Opera, intermittent service issues)
    if (error instanceof Error) {
      throw new NotificationPushServiceError(error.name, error.message);
    }
    throw new NotificationPushServiceError('Unknown', String(error));
  }

  if (!token) {
    throw new NotificationUnsupportedError('Failed to obtain FCM token');
  }

  return token;
}
